import { useEffect, useRef } from 'react';
import { useCustomer } from '../contexts/CustomerContext';
import useCart from './useCart';

/**
 * Auto-applies a resolved promotional offer coupon to the customer's cart, so the
 * discount is real by the time they reach checkout without them entering a code.
 *
 * Mirrors OfferableCoupon's apply: set couponId, save, refresh context. Uses the
 * MAIN cart (customer.cart), which — unlike apptCart — persists to localStorage,
 * so there's no reload/bounce hazard. Saves at most once per offer id (a ref
 * guards against the effect re-running once couponId round-trips back).
 *
 * NEVER replaces a coupon the customer already has: if the cart carries a different
 * couponId (e.g. a better code they typed at checkout, then navigated back through a
 * pricelist), the offer is NOT applied — we don't silently downgrade their choice.
 */
export default function useApplyOfferToCart(offer) {
  const { customer, setCustomer } = useCustomer();
  const { data: cart } = useCart(customer?.cart?.id);
  const appliedRef = useRef(null);

  useEffect(() => {
    if (!offer || !cart) return;
    if (cart.couponId === offer.id) return;
    // Respect an existing, different coupon — don't overwrite what the customer chose.
    if (cart.couponId) return;
    if (appliedRef.current === offer.id) return;

    appliedRef.current = offer.id;
    cart.couponId = offer.id;
    cart.save().then(() => {
      setCustomer((prev) => ({ ...prev, cart }));
    });
  }, [offer, cart, setCustomer]);
}
